import type { AppDispatch, RootState } from 'app/store';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { flattenErrors } from 'app/slices/ahuValidationSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { validateTunnel } from '../../validation/validationController';
import {
  buildSelects,
  transformErrors,
  calculateClearances,
  calculateOverlapAndChange,
  adjustAccessoryDimension,
  roundBasedOnArguments,
  CONNECT_ERROR,
  type ResolveListItem,
  type UpsSectionContext,
} from './resolveCatalog';
import { ACCESSORY_PLURALS } from '../tabs/TabAccessories';
import {
  ACCESSORY_FORM_CONFIGS,
  type AccessoryTypeName,
} from '../../forms/accessory/accessoryFormConfigs';

/**
 * Phase 6e1 — THE smart-resolve APPLY pipeline (§8.1.1,
 * error.log.resolution{,.control}.js collectErrorsByType port): categorize →
 * patch tunnels (increment-rounded) → patch sections (+auto_adjust:false) →
 * revalidate touched tunnels → re-resolve accessory errors against fresh
 * results → create upstream plenums → patch accessory positions/sizes (CMS
 * minimums + clearance math) → second tunnel/section sweep from accumulated
 * overflow → final revalidate → complete.
 *
 * Progress = weighted step counter (coefficients all 2, 99 %-capped, 100 on
 * complete — calculateCoefficients/calculateCompletedPercent verbatim).
 */

interface ResolveSelection {
  value: string;
  item: ResolveListItem;
}

interface AccessoryAction {
  x?: number;
  y?: number;
  height?: number;
  width?: number;
  length?: number;
  accessory_width?: number;
  accessory_height?: number;
  accessoryId: number;
  plural: string;
  positionId: number;
  tunnelId: number;
  sectionId: number;
}

interface ErrorBuckets {
  tunnel: Record<string, Record<string, number>>;
  section: Record<string, { length: number; tunnelId: number }>;
  accessory: Record<string, AccessoryAction>;
  upsPlenums: Record<string, { length: number; tunnelId: number; error: string }>;
}

const COEFFICIENTS = {
  tunnel: 2,
  section: 2,
  accessory: 2,
  validation: 2,
  upsPlenums: 2,
} as const;

export interface ApplyHandle {
  onProgress: (percent: number) => void;
}

export async function applyResolution(
  dispatch: AppDispatch,
  getState: () => RootState,
  resolve: ResolveSelection[],
  ctx: UpsSectionContext,
  handle: ApplyHandle
): Promise<{ needValidateTunnels: number[]; error: boolean }> {
  let completed = 0;
  let coefficient = 1;
  const progress = (steps: number, finished?: number) => {
    if (finished != null) {
      handle.onProgress(finished);
      return;
    }
    completed += steps;
    handle.onProgress(Math.min(99, Math.ceil(completed * coefficient)));
  };

  const getTunnels = (): IAhuBoardTunnel[] => {
    const state = getState();
    const queries = (state[ahuBoardApi.reducerPath] as {
      queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
    }).queries;
    for (const q of Object.values(queries)) {
      if (q?.endpointName === 'getAhuTunnelList' && Array.isArray(q.data)) {
        return q.data as IAhuBoardTunnel[];
      }
    }
    return [];
  };

  const getSections = (tunnelId: number): IAhuBoardSection[] => {
    const state = getState();
    const queries = (state[ahuBoardApi.reducerPath] as {
      queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
    }).queries;
    for (const [key, q] of Object.entries(queries)) {
      if (
        q?.endpointName === 'getTunnelSectionList' &&
        key.includes(`(${tunnelId})`) &&
        Array.isArray(q.data)
      ) {
        return q.data as IAhuBoardSection[];
      }
    }
    return [];
  };

  const errors: ErrorBuckets = { tunnel: {}, section: {}, accessory: {}, upsPlenums: {} };
  const needValidateTunnels: Record<number, boolean> = {};
  const updatedTunnels: Record<number, boolean> = {};
  const accessoryCmsCache: Record<string, Record<string, unknown>> = {};

  /**
   * The CMS params route is NOT the accessory's plural — it is irregular per
   * type, matching the legacy `*.cms.js` urlRoots verbatim:
   *   access_door -> /ahu/access_door/params/  (singular)
   *   opening     -> /ahu/opening/params/      (singular)
   *   hood        -> /ahu/hoods/params/        (plural)
   * `api/rest_cms/urls.py` includes those prefixes literally, so deriving the
   * URL from ACCESSORY_PLURALS 404s on access_door and opening. Take the URL
   * and response key from ACCESSORY_FORM_CONFIGS, which already carries both.
   */
  const getAccessoryCms = async (object: string): Promise<Record<string, unknown> | null> => {
    const cmsTypes = ['opening', 'hood', 'access_door'];
    if (!cmsTypes.includes(object)) return null;
    if (accessoryCmsCache[object]) return accessoryCmsCache[object];
    const url = ACCESSORY_FORM_CONFIGS[object as AccessoryTypeName]?.cmsAccessoryURL;
    if (!url) return null;
    try {
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then((r) => r.json());
      accessoryCmsCache[object] = res as Record<string, unknown>;
      return accessoryCmsCache[object];
    } catch {
      return null;
    }
  };

  const cmsMin = (object: string, dim: 'height' | 'width'): number => {
    const cms = accessoryCmsCache[object];
    const key = ACCESSORY_FORM_CONFIGS[object as AccessoryTypeName]?.cmsAccessoryKey ?? object;
    const entry = (cms?.[key] ?? cms) as Record<string, unknown> | undefined;
    const v = Number(entry?.[`min_${dim}`]);
    return Number.isFinite(v) && v > 0 ? v : 12;
  };

  // ---- categorize ---------------------------------------------------------
  const getUpsSectionId = (tunnelId: number, sectionId: number): number | null => {
    const tunnel = getTunnels().find((t) => t.id === tunnelId);
    const sections = getSections(tunnelId);
    const idx = sections.findIndex((s) => s.id === sectionId);
    if (idx === -1) return null;
    const delta = tunnel?.airflow_direction_id === 1 ? -1 : 1;
    return sections[idx + delta]?.id ?? null;
  };

  const handleAccessoryError = async (e: ResolveSelection) => {
    const { item } = e;
    const accessoryId = Number(item.accessory_id ?? item.id);
    const object = String(item.object ?? '');
    const plural = ACCESSORY_PLURALS[object] ?? `${object}s`;
    await getAccessoryCms(object);
    const [position, accessory] = await Promise.all([
      dispatch(
        ahuBoardApi.endpoints.getAccessoryPosition.initiate(accessoryId, {
          forceRefetch: true,
        })
      ).unwrap(),
      dispatch(
        ahuBoardApi.endpoints.getAccessoryDetail.initiate(
          { plural, id: accessoryId },
          { forceRefetch: true }
        )
      ).unwrap(),
    ]);

    const value = Number(item.value) || 0;
    const posX = Number(position.x) || 0;
    const posY = Number(position.y) || 0;
    const planeId = position.mounting_plane_id ?? null;
    const xOrY: 'x' | 'y' = planeId === 1 || planeId === 2 ? 'y' : 'x';
    const posAxis = xOrY === 'y' ? posY : posX;
    const clear = calculateClearances(
      (accessory.clearances ?? {}) as Record<string, number>,
      planeId
    );
    const accHeight = Number(accessory.height) || 0;
    const accWidth = Number(accessory.width) || 0;
    const distRoof = Number(accessory.distance_to_roof) || 0;
    const distRight = Number(accessory.distance_to_right_wall) || 0;
    const distDowns = Number(accessory.distance_to_downstream_wall) || 0;

    const incTunnelHeight = (y: number) => {
      const newY = Math.max(y, clear.btm);
      const required =
        newY > posY
          ? calculateOverlapAndChange(newY, posY, distRoof, clear.top)
          : value - (posY - newY);
      return { y: newY, height: required || 0 };
    };
    const incTunnelWidth = (val: number) => {
      const newVal = Math.max(val, clear.left_right);
      const required =
        newVal > posAxis
          ? calculateOverlapAndChange(newVal, posAxis, distRight, clear.left_right)
          : value - (posAxis - newVal);
      return { [xOrY]: newVal, width: required } as Partial<AccessoryAction>;
    };
    const incSectionLength = (x: number) => {
      const upstream = clear.ups || clear.ups_down;
      const downstream = clear.downs || clear.ups_down;
      const newX = Math.max(x, upstream);
      const required =
        newX > posX
          ? calculateOverlapAndChange(newX, posX, distDowns, downstream)
          : value - (posX - newX);
      return { x: newX, length: required };
    };
    const reduceHeight = (y: number, incTunnel: boolean) => {
      const newY = Math.max(y, clear.btm);
      const required =
        newY > posY
          ? calculateOverlapAndChange(newY, posY, distRoof, clear.top)
          : value - (posY - newY);
      const { newSize, extraChange } = adjustAccessoryDimension(
        accHeight,
        required,
        cmsMin(object, 'height')
      );
      const result: Partial<AccessoryAction> = { y: newY, accessory_height: newSize };
      if (incTunnel && extraChange > 0) result.height = extraChange;
      return result;
    };
    const reduceWidth = (val: number, incTunnel: boolean) => {
      const newVal = Math.max(val, clear.left_right);
      const required =
        newVal > posAxis
          ? calculateOverlapAndChange(newVal, posAxis, distRight, clear.left_right)
          : value - (posAxis - newVal);
      const { newSize, extraChange } = adjustAccessoryDimension(
        accWidth,
        required,
        cmsMin(object, 'width')
      );
      const result: Partial<AccessoryAction> = {
        [xOrY]: newVal,
        accessory_width: newSize,
      } as Partial<AccessoryAction>;
      if (incTunnel && extraChange > 0) result.width = extraChange;
      return result;
    };
    const reduceWidthIncLength = (x: number, incLength: boolean) => {
      const upstream = clear.ups || clear.ups_down;
      const downstream = clear.downs || clear.ups_down;
      const newX = Math.max(x, upstream);
      const dimKey = xOrY === 'x' ? 'width' : 'height';
      const required =
        newX > posX
          ? calculateOverlapAndChange(newX, posX, distDowns, downstream)
          : value - (posX - newX);
      const current = dimKey === 'width' ? accWidth : accHeight;
      const { newSize, extraChange } = adjustAccessoryDimension(
        current,
        required,
        cmsMin(object, dimKey)
      );
      const result: Partial<AccessoryAction> = {
        x: newX,
        [`accessory_${dimKey}`]: newSize,
      } as Partial<AccessoryAction>;
      if (incLength && extraChange > 0) result.length = extraChange;
      return result;
    };

    const actions: Record<string, Partial<AccessoryAction>> = {
      move_top: { y: posY + value },
      move_btm: { y: posY - value },
      move_down: { x: posX + value },
      move_up: { x: posX - value },
      move_left: { x: posX - value },
      move_right: { x: posX + value },
      move_top_reduce: reduceHeight(posY + value, false),
      move_btm_reduce: reduceHeight(posY - value, false),
      move_top_inc_tunnel_height: incTunnelHeight(posY + value),
      move_top_reduce_inc_tunnel_height: reduceHeight(posY - value, true),
      move_btm_reduce_inc_tunnel_height: reduceHeight(posY - value, true),
      move_btm_inc_tunnel_height: incTunnelHeight(posY - value),
      move_right_inc_tunnel_width: incTunnelWidth(posAxis + value),
      move_left_inc_tunnel_width: incTunnelWidth(posAxis - value),
      move_down_inc_section_length: incSectionLength(posX + value),
      move_ups_inc_section_length: incSectionLength(posX - value),
      move_right_reduce_inc_tunnel_width: reduceWidth(posAxis + value, true),
      move_right_reduce: reduceWidth(posAxis + value, false),
      move_left_reduce_inc_tunnel_width: reduceWidth(posAxis - value, true),
      move_left_reduce: reduceWidth(posAxis - value, false),
      move_down_reduce_inc_section_length: reduceWidthIncLength(posX + value, true),
      move_down_reduce: reduceWidthIncLength(posX + value, false),
      move_ups_reduce_inc_section_length: reduceWidthIncLength(posX - value, true),
      move_ups_reduce: reduceWidthIncLength(posX - value, false),
    };

    const data = actions[e.value];
    if (!data) return;
    const tunnelId = Number(item.tunnel_id);
    const sectionId = Number(item.section_id);
    const full: AccessoryAction = {
      ...data,
      accessoryId,
      plural,
      positionId: Number(position.id),
      tunnelId,
      sectionId,
    };
    errors.accessory[accessoryId] ??= full;
    const acc = errors.accessory[accessoryId];
    (
      ['x', 'y', 'height', 'length', 'width', 'accessory_width', 'accessory_height'] as const
    ).forEach((metric) => {
      const v = data[metric];
      if (!v) return;
      if (metric === 'height' || metric === 'width') {
        errors.tunnel[tunnelId] ??= {};
        errors.tunnel[tunnelId][`does_not_fit_in_${metric}`] = Math.max(
          errors.tunnel[tunnelId][`does_not_fit_in_${metric}`] || 0,
          v
        );
      }
      if (metric === 'length') {
        if (!errors.section[sectionId]) {
          errors.section[sectionId] = { length: v, tunnelId };
        } else {
          errors.section[sectionId].length = Math.max(errors.section[sectionId].length, v);
        }
      }
      acc[metric] = Math.max(acc[metric] || 0, v);
    });
  };

  const categorizeError = async (e: ResolveSelection) => {
    const { item } = e;
    const value = Number(item.value) || 0;
    const tunnelId = Number(item.tunnel_id);
    const sectionId = Number(item.section_id);
    const errorKey = String(item.error ?? '');
    const selects = buildSelects(item.item);
    const type = e.value === 'inc_length' ? 'section' : selects[errorKey]?.type;

    if (type === 'tunnel') {
      errors.tunnel[tunnelId] ??= {};
      errors.tunnel[tunnelId][errorKey] = Math.max(
        errors.tunnel[tunnelId][errorKey] || 0,
        value
      );
    } else if (type === 'section') {
      if (e.value === 'inc_length') {
        // An accessory short of BOTH its upstream and downstream clearance
        // raises two errors; transformErrors keeps the downstream row and
        // marks the upstream one "Error will be resolved through another
        // error", parking its shortfall on `extra_value`. The section has to
        // grow by BOTH, or the clearance the suppressed row stood for is never
        // made up: the server re-centres an auto-positioned accessory
        // (`AHUAccessory.set_position`), so a section grown by one side's
        // shortfall gives each side only half of it and the pair comes back
        // halved -- 6.00 -> 3.00 -> 1.50, never zero, which reads as "Resolve
        // did nothing". `value + extra_value` is the same total the
        // move_ups_inc_section_length branch arrives at from the geometry
        // (`incSectionLength`), so both options now land on one length.
        // `extra_value` is unset on an unpaired error, and on the section-level
        // errors that share this option (error_min_clr_ups_inc, ...), which the
        // accessory pairing never touches.
        const required = value + (Number(item.extra_value) || 0);
        if (!errors.section[sectionId]) {
          errors.section[sectionId] = { length: required, tunnelId };
        } else {
          errors.section[sectionId].length = Math.max(
            errors.section[sectionId].length,
            required
          );
        }
      } else if (e.value === 'add_plenum') {
        if (!errors.upsPlenums[sectionId]) {
          errors.upsPlenums[sectionId] = { length: value, tunnelId, error: errorKey };
        } else {
          errors.upsPlenums[sectionId].length = Math.max(
            errors.upsPlenums[sectionId].length,
            value
          );
        }
      } else if (e.value === 'inc_ups_length') {
        const upsId = getUpsSectionId(tunnelId, sectionId);
        if (upsId != null) {
          if (!errors.section[upsId]) {
            errors.section[upsId] = { length: value, tunnelId };
          } else {
            errors.section[upsId].length = Math.max(errors.section[upsId].length, value);
          }
        }
      }
    } else if (type === 'accessory') {
      await handleAccessoryError(e);
    }
  };

  // ---- patches ------------------------------------------------------------
  const patchTunnelStep = async (tunnelId: number, adjustments: Record<string, number>) => {
    const tunnel = getTunnels().find((t) => t.id === tunnelId);
    if (!tunnel) return;
    const height = adjustments.does_not_fit_in_height
      ? adjustments.does_not_fit_in_height + Number(tunnel.height)
      : undefined;
    const width = adjustments.does_not_fit_in_width
      ? adjustments.does_not_fit_in_width + Number(tunnel.width)
      : undefined;
    updatedTunnels[tunnelId] = true;
    progress(1);
    await dispatch(
      ahuBoardApi.endpoints.patchAhuTunnel.initiate({
        id: tunnelId,
        ahuId: tunnel.ahu_id,
        data: {
          width: width
            ? roundBasedOnArguments(width, Number(tunnel.width_increment) || 1)
            : Number(tunnel.width),
          height: height
            ? roundBasedOnArguments(height, Number(tunnel.height_increment) || 1)
            : Number(tunnel.height),
        },
      })
    ).unwrap();
    progress(COEFFICIENTS.tunnel);
  };

  const patchSectionStep = async (
    sectionId: number,
    tunnelId: number,
    extraLength: number
  ) => {
    const section = getSections(tunnelId).find((s) => s.id === sectionId);
    if (!section) return;
    updatedTunnels[tunnelId] = true;
    progress(1);
    await dispatch(
      ahuBoardFormsApi.endpoints.patchAhuSection.initiate({
        id: sectionId,
        tunnelId,
        data: {
          length: roundBasedOnArguments(
            Number(section.length) + extraLength,
            Number(section.increment) || 1
          ),
          auto_adjust: false,
        },
      })
    ).unwrap();
    progress(COEFFICIENTS.section);
  };

  const patchAccessoryStep = async (actions: AccessoryAction) => {
    progress(1);
    if (actions.accessory_height || actions.accessory_width) {
      const data: Record<string, number> = {};
      if (actions.accessory_height) data.height = actions.accessory_height;
      if (actions.accessory_width) data.width = actions.accessory_width;
      await dispatch(
        ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
          plural: actions.plural,
          id: actions.accessoryId,
          data,
        })
      ).unwrap();
    }
    const pos: Record<string, unknown> = {};
    if (actions.x !== undefined) {
      pos.x = actions.x;
      pos.default_pos_x = false;
    }
    if (actions.y !== undefined) {
      pos.y = actions.y;
      pos.default_pos_y = false;
    }
    if (Object.keys(pos).length) {
      await dispatch(
        ahuBoardFormsApi.endpoints.patchAhuPosition.initiate({
          id: actions.positionId,
          data: pos,
        })
      ).unwrap();
    }
    progress(COEFFICIENTS.accessory);
  };

  const createPlenumStep = async (
    sectionId: number,
    tunnelId: number,
    sectionLength: number
  ) => {
    const section = getSections(tunnelId).find((s) => s.id === sectionId);
    progress(1);
    await dispatch(
      ahuBoardFormsApi.endpoints.createAhuSection.initiate({
        // Legacy passed "plenum" only as the CANVAS name (error.log.
        // resolution.js:225 → api.section.create); the wire tag was the model
        // default '' — 'plenum' here surfaced a " : plenum" row suffix legacy
        // never showed (QA batch 9 #2).
        tag: '',
        tunnel_id: tunnelId,
        length: roundBasedOnArguments(sectionLength, Number(section?.increment) || 1),
        order_rate: section?.order_rate ?? 1,
      })
    ).unwrap();
    progress(COEFFICIENTS.upsPlenums);
  };

  const validateAllTouched = async (touched: Record<number, boolean>) => {
    const fresh: Awaited<ReturnType<typeof flattenErrors>> = [];
    for (const id of Object.keys(touched).map(Number)) {
      progress(1);
      const res = await dispatch(validateTunnel(id));
      progress(COEFFICIENTS.validation);
      if (res) {
        fresh.push(...flattenErrors([res]));
      }
    }
    return fresh.length ? fresh : null;
  };

  // ---- pipeline -----------------------------------------------------------
  try {
    for (const e of resolve) {
      await categorizeError(e);
    }

    // calculateCoefficients.
    let time = 0;
    (Object.keys(errors) as Array<keyof ErrorBuckets>).forEach((key) => {
      const n = Object.keys(errors[key]).length;
      time += n * COEFFICIENTS[key === 'upsPlenums' ? 'upsPlenums' : key] + n;
    });
    time += COEFFICIENTS.validation * Object.keys(errors.tunnel).length +
      Object.keys(errors.tunnel).length;
    coefficient = time > 0 ? 99 / time : 99;

    for (const tunnelId of Object.keys(errors.tunnel)) {
      await patchTunnelStep(Number(tunnelId), errors.tunnel[tunnelId]);
      needValidateTunnels[Number(tunnelId)] = true;
    }
    for (const sectionId of Object.keys(errors.section)) {
      await patchSectionStep(
        Number(sectionId),
        errors.section[sectionId].tunnelId,
        errors.section[sectionId].length
      );
      needValidateTunnels[errors.section[sectionId].tunnelId] = true;
    }

    if (Object.keys(errors.section).length + Object.keys(errors.tunnel).length > 0) {
      const newErrors = await validateAllTouched(needValidateTunnels);
      errors.accessory = {};
      if (newErrors) {
        const { list: transformed } = transformErrors(newErrors, ctx);
        await new Promise((r) => setTimeout(r, 2000));
        // compare_errors — refresh upsPlenums against fresh results.
        Object.keys(errors.upsPlenums).forEach((sectionId) => {
          const entry = errors.upsPlenums[sectionId];
          const filtered = transformed
            .filter(
              (t) =>
                Number(t.section_id) === Number(sectionId) &&
                Number(t.tunnel_id) === entry.tunnelId &&
                t.error === entry.error
            )
            .map((t) => Number(t.value));
          if (!filtered.length) delete errors.upsPlenums[sectionId];
          else entry.length = Math.max(...filtered);
        });
        // re-resolve accessory errors against fresh results.
        const accessoriesResolve = resolve
          .map(({ item, value }) => {
            const match = transformed.find(
              (a) =>
                a.accessory_id &&
                a.accessory_id === item.accessory_id &&
                (a.error === item.error || CONNECT_ERROR[a.error ?? ''] === item.error)
            );
            return match ? { value, item: match } : null;
          })
          .filter((x): x is ResolveSelection => !!x);
        for (const e of accessoriesResolve) {
          await categorizeError(e);
        }
      } else {
        errors.upsPlenums = {};
      }
    }

    const secondSweep: Record<number, boolean> = {};
    for (const sectionId of Object.keys(errors.upsPlenums)) {
      await createPlenumStep(
        Number(sectionId),
        errors.upsPlenums[sectionId].tunnelId,
        errors.upsPlenums[sectionId].length
      );
      secondSweep[errors.upsPlenums[sectionId].tunnelId] = true;
    }

    const changes = {
      width: {} as Record<number, number>,
      height: {} as Record<number, number>,
      length: {} as Record<number, Record<number, number>>,
    };
    for (const accessoryId of Object.keys(errors.accessory)) {
      const actions = errors.accessory[accessoryId];
      await patchAccessoryStep(actions);
      (['height', 'width'] as const).forEach((key) => {
        const v = actions[key];
        if (v && (changes[key][actions.tunnelId] || 0) < v) {
          changes[key][actions.tunnelId] = v;
        }
      });
      if (actions.length) {
        changes.length[actions.tunnelId] ??= {};
        changes.length[actions.tunnelId][actions.sectionId] = Math.max(
          changes.length[actions.tunnelId][actions.sectionId] || 0,
          actions.length || 0
        );
      }
      secondSweep[actions.tunnelId] = true;
    }

    const tunnelAdjust: Record<number, Record<string, number>> = {};
    (['height', 'width'] as const).forEach((key) => {
      Object.keys(changes[key]).forEach((id) => {
        tunnelAdjust[Number(id)] ??= {};
        tunnelAdjust[Number(id)][`does_not_fit_in_${key}`] = Math.max(
          tunnelAdjust[Number(id)][`does_not_fit_in_${key}`] || 0,
          changes[key][Number(id)]
        );
      });
    });
    const sectionAdjust: Record<number, { length: number; tunnelId: number }> = {};
    Object.keys(changes.length).forEach((tunnelId) => {
      Object.keys(changes.length[Number(tunnelId)]).forEach((id) => {
        sectionAdjust[Number(id)] = {
          length: Math.max(
            sectionAdjust[Number(id)]?.length || 0,
            changes.length[Number(tunnelId)][Number(id)]
          ),
          tunnelId: Number(tunnelId),
        };
      });
    });
    for (const id of Object.keys(tunnelAdjust)) {
      await patchTunnelStep(Number(id), tunnelAdjust[Number(id)]);
      secondSweep[Number(id)] = true;
    }
    for (const id of Object.keys(sectionAdjust)) {
      await patchSectionStep(
        Number(id),
        sectionAdjust[Number(id)].tunnelId,
        sectionAdjust[Number(id)].length
      );
      secondSweep[sectionAdjust[Number(id)].tunnelId] = true;
    }

    await new Promise((r) => setTimeout(r, 1000));
    await validateAllTouched(secondSweep);
    await new Promise((r) => setTimeout(r, 1000));

    progress(0, 100);
    const touched = { ...updatedTunnels };
    Object.keys(touched).forEach((tunnelId) => {
      ahuBoardBus.emit('change:tunnel:dimensions', {
        tunnel_id: Number(tunnelId),
        render: true,
      });
    });
    return { needValidateTunnels: Object.keys(secondSweep).map(Number), error: false };
  } catch (err) {
    // Error path: revalidate + surface "Something went wrong!".
    await validateAllTouched(needValidateTunnels).catch(() => null);
    return { needValidateTunnels: Object.keys(needValidateTunnels).map(Number), error: true };
  }
}
